
import { computed, defineComponent, reactive } from 'vue'
import { useRoute } from 'vue-router'

// components
import Cheap from '@/common/components/UI/Cheap/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'
import { useFile } from '@/common/composable/useFile'
import { contracts } from '@/core/realisations/contracts.realisation'

export default defineComponent({
    components: {
        Cheap,
        ButtonDropdown,
        TheButton,
    },
    props: {
        carrierId: {
            type: Number,
            default: null,
        },
        orderStatusCode: {
            type: String,
            default: null,
        },
        orderStatus: {
            type: String,
            default: null,
        },
        isBillingEnabled: {
            type: Boolean,
            default: false,
        },
        isIntracity: {
            type: Boolean,
            default: false,
        },
        isEdit: {
            type: Boolean,
            default: false,
        },
        isInternationalTransportation: {
            type: Boolean,
            default: false,
        },
        ftlBidId: {
            type: Number,
            default: null,
        },
        paymentBySuppliersId: {
            type: Number,
            default: null,
        },
        logistBidId: {
            type: Number,
            default: null,
        },
        logistBidStatusId: {
            type: Number,
            default: null,
        },
        scan: {
            type: String,
            default: null,
        },
        isStatusLoading: {
            type: Boolean,
            default: false,
        },
        isCompleteLoading: {
            type: Boolean,
            default: false,
        },
        isDeliveredLoading: {
            type: Boolean,
            default: false,
        },
        isTranscriptPrinting: {
            type: Boolean,
            default: false,
        },
        isRoutePrinting: {
            type: Boolean,
            default: false,
        },
        isResendingToOneC: {
            type: Boolean,
            default: false,
        },
        isInvoicesPrinting: {
            type: Boolean,
            default: false,
        },
        alreadyHaveOneC: {
            type: Boolean,
            default: false,
        },
        isVacationInvoiceUploading: {
            type: Boolean,
            default: false,
        },
        isCarrierLoading: {
            type: Boolean,
            default: false,
        },
        isNeedCar: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'open-bid-create',
        'assign-status-on-the-way',
        'assign-status-export-declaration-issued',
        'assign-status-crossing-the-border',
        'assign-status-delivered',
        'assign-status-completed',
        'print-transcript',
        'print-route',
        'resend-to-one-c',
        'assign-status-canceled',
        'print-invoices',
        'download-scan',
        'upload-scan',
        'upload-vacation-invoice',
        'carrier',
        'delivered',
        'complete-intracity',
    ],
    setup(props, { emit }) {
        const route = useRoute()
        const { havePermissions } = useAccount()

        const state = reactive({
            is_loading: false,
        })

        const assignStatusOnTheWay = () => {
            emit('assign-status-on-the-way')
        }

        const assignStatusExportDeclarationIssued = () => {
            emit('assign-status-export-declaration-issued')
        }

        const assignStatusCrossingTheBorder = () => {
            emit('assign-status-crossing-the-border')
        }

        const assignStatusDelivered = () => {
            emit('assign-status-delivered')
        }

        const assignStatusCompleted = () => {
            emit('assign-status-completed')
        }

        const onPrintTranscript = () => {
            emit('print-transcript')
        }

        const onPrintRoute = () => {
            emit('print-route')
        }

        const onResendToOneC = () => {
            emit('resend-to-one-c')
        }

        const assignStatusCanceled = () => {
            emit('assign-status-canceled')
        }

        const onPrintInvoices = () => {
            emit('print-invoices')
        }

        const onDownloadScan = () => {
            emit('download-scan')
        }

        const onUploadScan = () => {
            emit('upload-scan')
        }

        const onUploadVacationInvoice = () => {
            emit('upload-vacation-invoice')
        }

        const emitCarrier = () => {
            emit('carrier')
        }

        const emitDelivered = () => {
            emit('delivered')
        }

        const emitCompleteIntracity = () => {
            emit('complete-intracity')
        }

        const ftlOrderStatusCheapClass = computed(() => {
            return {
                'blue--outline': props.orderStatusCode === 'created',
                yellow: [
                    'wait_cost_price',
                    'on_updating',
                    'on_approval',
                    'carrier_data_entered',
                    'approved',
                    'on_inspection_head_transport',
                    'logist_bid_signed',
                    'processing_logist_bid',
                ].includes(props.orderStatusCode),
                'yellow--outline': props.orderStatusCode === 'on_the_way',
                'yellow--fill': props.orderStatusCode === 'exported_declaration',
                green: props.orderStatusCode === 'confirmed',
                'green--outline': props.orderStatusCode === 'completed',
                'green--fill': props.orderStatusCode === 'delivered',
                red: props.orderStatusCode === 'denied',
                'red--outline': props.orderStatusCode === 'canceled',
                'orange--outline': props.orderStatusCode === 'crossed_the_border',
            }
        })

        const isCanUpdateFtlOrder = computed(() => {
            return havePermissions(['all-root-permission', 'order-ftl-update']) && !route.path.includes('edit')
        })

        const isOrderStatusCanceled = computed(() => {
            // 'Отменен'
            return props.orderStatusCode === 'canceled'
        })

        const isOrderStatusCompleted = computed(() => {
            // 'Завершен'
            return props.orderStatusCode === 'completed'
        })

        const isCanUpdateFtlIntracityOrder = computed(() => {
            return (
                havePermissions(['all-root-permission']) ||
                (havePermissions(['order-ftl-update']) && !isOrderStatusCompleted.value)
            )
        })

        const isOrderStatusDelivered = computed(() => {
            // 'Доставлен'
            return props.orderStatusCode === 'delivered'
        })

        const isCanCancelFtlOrder = computed(() => {
            return (
                !isOrderStatusDelivered.value &&
                !isOrderStatusCompleted.value &&
                !isOrderStatusCanceled.value &&
                havePermissions(['cabinet-ftl-order-change-status', 'all-root-permission'])
            )
        })

        const isCanCancelFtlIntracityOrder = computed(() => {
            return (
                havePermissions(['all-root-permission']) ||
                (!isOrderStatusDelivered.value &&
                    !isOrderStatusCompleted.value &&
                    !isOrderStatusCanceled.value &&
                    havePermissions(['cabinet-ftl-order-change-status']))
            )
        })

        const isCanUploadScan = computed(() => {
            return havePermissions(['cabinet-flt-order-upload-scan', 'all-root-permission'])
        })

        /**
         * Order Status
         */
        const isOrderStatusCreated = computed(() => {
            // 'Создан'
            return props.orderStatusCode === 'created'
        })

        const isOrderStatusOnTheWay = computed(() => {
            // 'В пути'
            return props.orderStatusCode === 'on_the_way'
        })

        const isOrderStatusExportDeclarationIssued = computed(() => {
            // 'Оформлена экспортная декларация'
            return props.orderStatusCode === 'exported_declaration'
        })

        const isOrderStatusCrossingTheBorder = computed(() => {
            // 'Переход границы ТС'
            return props.orderStatusCode === 'crossed_the_border'
        })

        const isLogistBidSigned = computed(() => {
            // 'ЗЛ подписана'
            return props.orderStatusCode === 'logist_bid_signed'
        })

        /**
         * Is Can Assign
         */

        const isCanAssignStatusOnTheWay = computed(() => {
            return (
                isOrderStatusCreated.value &&
                havePermissions(['cabinet-ftl-order-change-status', 'all-root-permission'])
            )
        })

        const isCanAssignStatusExportDeclarationIssued = computed(() => {
            return isOrderStatusOnTheWay.value && props.isInternationalTransportation
        })

        const isCanAssignStatusCrossingTheBorder = computed(() => {
            return isOrderStatusExportDeclarationIssued.value && props.isInternationalTransportation
        })

        const isCanAssignStatusInternationalDelivered = computed(() => {
            return isOrderStatusCrossingTheBorder.value && props.isInternationalTransportation
        })

        const isCanAssignStatusDelivered = computed(() => {
            return (
                isOrderStatusOnTheWay.value &&
                !props.isInternationalTransportation &&
                havePermissions(['cabinet-ftl-order-delivery', 'all-root-permission'])
            )
        })

        const isCanAssignStatusCompleted = computed(() => {
            return (
                isOrderStatusDelivered.value &&
                havePermissions(['cabinet-ftl-order-complete', 'all-root-permission'])
            )
        })

        const isCanAssignStatuses = computed(() => {
            return (
                isCanAssignStatusOnTheWay.value ||
                isCanAssignStatusExportDeclarationIssued.value ||
                isCanAssignStatusCrossingTheBorder.value ||
                isCanAssignStatusInternationalDelivered.value ||
                isCanAssignStatusDelivered.value ||
                isCanAssignStatusCompleted.value
            )
        })

        const getTitle = computed(() => {
            if (props.isIntracity) {
                if (props.isEdit) {
                    return 'Редактирование FTL ВНГ заказа'
                }

                return 'FTL ВНГ заказ'
            }

            if (props.isEdit) {
                return 'Редактирование FTL заказа'
            }

            return 'FTL заказ'
        })

        const notifications = useNotifications()
        const { saveFile } = useFile()

        const downloadLogistBid = async () => {
            if (state.is_loading || !props.logistBidId) {
                return
            }

            try {
                state.is_loading = true

                const response = await contracts.downloadLogistBid(props.logistBidId)
                saveFile(response, `Заявка логиста ${props.logistBidId}`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При скачивании произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const toggleBidCreate = () => {
            emit('open-bid-create')
        }

        return {
            state,
            assignStatusOnTheWay,
            assignStatusExportDeclarationIssued,
            assignStatusCrossingTheBorder,
            assignStatusDelivered,
            assignStatusCompleted,
            onPrintTranscript,
            onPrintRoute,
            onResendToOneC,
            assignStatusCanceled,
            onPrintInvoices,
            onDownloadScan,
            onUploadScan,
            onUploadVacationInvoice,
            emitCarrier,
            emitDelivered,
            emitCompleteIntracity,
            downloadLogistBid,
            //
            ftlOrderStatusCheapClass,
            isCanUpdateFtlOrder,
            isCanUpdateFtlIntracityOrder,
            isCanCancelFtlOrder,
            isCanCancelFtlIntracityOrder,
            isCanUploadScan,
            isCanAssignStatusOnTheWay,
            isCanAssignStatusExportDeclarationIssued,
            isCanAssignStatusCrossingTheBorder,
            isCanAssignStatusInternationalDelivered,
            isCanAssignStatusDelivered,
            isCanAssignStatusCompleted,
            isCanAssignStatuses,
            isOrderStatusCreated,
            isLogistBidSigned,
            getTitle,
            toggleBidCreate,
        }
    },
})
